import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Icon,
  OptionsMenu,
  OptionButton,
  OptionList,
  OptionItem,
  MegaOptionsItems,
} from '..';
import { User } from '../../context/authContext';
import { paths } from '../../routes';
import isActiveRoute from '../../utils/isActiveRoute';

type NavigationProps = {
  user: User | null;
  isShowingTourGuide: boolean;
  onSignOut: () => void;
};

function Navigation({ user, onSignOut, isShowingTourGuide }: NavigationProps) {
  const navigate = useNavigate();

  return (
    <header
      className="bg-pos-yellow text-pos-black fixed 
    inset-x-0 top-0 z-50 flex-none min-h-[60px]"
    >
      <nav className="px-8">
        <div className="flex items-center justify-between h-[60px]">
          <div className="flex items-center">
            <Link
              to={paths.DASHBOARD_URL_PATH}
              className="shrink-0 font-semibold text-xl"
            >
              Swiftinflow
            </Link>
          </div>

          <div className="h-full flex flex-row items-center text-xbase laptop:space-x-4 laptop:divide-x-[1.5px] laptop:divide-[#8f7319]">
            <div className="hidden laptop:flex flex-row space-x-5">
              <button
                type="button"
                onClick={() =>
                  isShowingTourGuide
                    ? window.location.assign(paths.DASHBOARD_URL_PATH)
                    : navigate(paths.DASHBOARD_URL_PATH)
                }
                className={`hidden tablet:flex flex-row items-center justify-center shrink-0 border-[1.5px] border-[#8f7319] 
                rounded-md px-3 h-[35px] cursor-pointer min-w-[133px] group hover:border-pos-black hover:bg-pos-black 
                hover:text-pos-yellow ${isActiveRoute('dashboard')} `}
              >
                <div className="h-[20px] w-[20px] mr-1">
                  <Icon
                    id="iconDashboardNav"
                    className="h-full w-full object-cover fill-[#8f7319] group-hover:fill-pos-yellow"
                  />
                </div>
                <span className="leading-[22px] inline-block text-[#8f7319] group-hover:font-medium group-hover:text-pos-yellow cursor-pointer">
                  Dashboard
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate(paths.POS_TELLER_URL_PATH)}
                className={`hidden tablet:flex flex-row items-center justify-center shrink-0 border-[1.5px] border-[#8f7319] rounded-md px-3
                h-[35px] cursor-pointer min-w-[133px] group hover:border-pos-black hover:bg-pos-black hover:text-pos-yellow 
                ${isActiveRoute('pos-teller')} `}
              >
                <div className="h-[20px] w-[20px] mr-1">
                  <Icon
                    id="iconPOSNav"
                    className="h-full w-full object-cover fill-[#8f7319] group-hover:fill-pos-yellow"
                  />
                </div>
                <span className="leading-[22px] inline-block text-[#8f7319] group-hover:font-medium group-hover:text-pos-yellow cursor-pointer">
                  POS Teller
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate(paths.INVENTORIES_URL_PATH)}
                className={`hidden tablet:flex flex-row items-center justify-center shrink-0 border-[1.5px] border-[#8f7319] rounded-md px-3
                h-[35px] cursor-pointer min-w-[133px] group hover:border-pos-black hover:bg-pos-black hover:text-pos-yellow 
                ${isActiveRoute('inventory')} `}
              >
                <div className="h-[20px] w-[20px] mr-1">
                  <Icon
                    id="iconProductNav"
                    className="h-full w-full object-cover fill-[#8f7319] group-hover:fill-pos-yellow"
                  />
                </div>
                <span className="leading-[22px] inline-block text-[#8f7319] group-hover:font-medium group-hover:text-pos-yellow cursor-pointer">
                  Inventory
                </span>
              </button>

              {['Admin', 'Webmaster'].includes(user?.role || '') && (
                <>
                  <OptionsMenu className="relative hidden tablet:block">
                    <OptionButton
                      className="flex flex-row items-center justify-center shrink-0 border-[1.5px] border-[#8f7319] rounded-md px-3
                  h-[35px] cursor-pointer min-w-[133px] group hover:border-pos-black hover:bg-pos-black hover:text-pos-yellow"
                    >
                      <div className="h-[20px] w-[20px] mr-1">
                        <Icon
                          id="iconReportsNav"
                          className="h-full w-full object-cover fill-[#8f7319] group-hover:fill-pos-yellow"
                        />
                      </div>
                      <span className="leading-[22px] inline-block text-[#8f7319] group-hover:font-medium group-hover:text-pos-yellow cursor-pointer">
                        Reporting
                      </span>
                    </OptionButton>
                    <OptionList
                      className="!min-w-max mt-5 rounded-xl shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] left-[50%] translate-x-[-50%]
                  bg-white border-[0.5px] border-[#e9ecef] ring-[#e9ecef] ring-opacity-5 focus:outline-none"
                    >
                      <OptionItem
                        className="text-gray-700"
                        onClick={() =>
                          navigate(paths.SALE_RECEIPTS_REPORT_URL_PATH)
                        }
                      >
                        Sale Receipts
                      </OptionItem>
                      {/* <OptionItem
                        className="text-gray-700"
                        onClick={() =>
                          navigate(paths.SUMMARY_SALES_REPORT_URL_PATH)
                        }
                      >
                        Summary of Sales
                      </OptionItem> */}
                      <OptionItem
                        className=" text-gray-700"
                        onClick={() =>
                          navigate(paths.DETAILED_SALES_REPORT_URL_PATH)
                        }
                      >
                        Detailed Sales Report
                      </OptionItem>
                      <OptionItem
                        className=" text-gray-700"
                        onClick={() =>
                          navigate(paths.STORE_INVENTORY_REPORT_URL_PATH)
                        }
                      >
                        Store Inventory COGS
                      </OptionItem>
                    </OptionList>
                  </OptionsMenu>

                  <OptionsMenu className="relative hidden tablet:block">
                    <OptionButton
                      className={`flex flex-row items-center justify-center shrink-0 border-[1.5px] border-[#8f7319] rounded-md px-3
                  h-[35px] cursor-pointer min-w-[133px] group hover:border-pos-black hover:bg-pos-black hover:text-pos-yellow 
                  ${isActiveRoute('preferences')} `}
                    >
                      <div className="h-[20px] w-[20px] mr-1">
                        <Icon
                          id="iconSettingsNav"
                          className="h-full w-full object-cover fill-[#8f7319] group-hover:fill-pos-yellow"
                        />
                      </div>
                      <span className="leading-[22px] inline-block text-[#8f7319] group-hover:font-medium group-hover:text-pos-yellow cursor-pointer">
                        Preferences
                      </span>
                    </OptionButton>
                    <OptionList
                      className="!min-w-max mt-5 rounded-xl shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] right-0
                  bg-white border-[0.5px] border-[#e9ecef] ring-[#e9ecef] ring-opacity-5 focus:outline-none"
                    >
                      <MegaOptionsItems
                        onItemClick={path => navigate(path)}
                        storeName={user?.defaultStore?.name}
                      />
                    </OptionList>
                  </OptionsMenu>
                </>
              )}

              {!['Admin', 'Webmaster'].includes(user?.role || '') && (
                <button
                  type="button"
                  onClick={() =>
                    window.location.assign(paths.CASH_REGISTER_TELLER_URL_PATH)
                  }
                  className={`hidden tablet:flex flex-row items-center justify-center shrink-0 border-[1.5px] border-[#8f7319] rounded-md px-3
                h-[35px] cursor-pointer min-w-[133px] group hover:border-pos-black hover:bg-pos-black hover:text-pos-yellow 
                ${isActiveRoute('preferences')} `}
                >
                  <div className="h-[20px] w-[20px] mr-1">
                    <Icon
                      id="iconCash"
                      className="h-full w-full object-cover fill-[#8f7319] group-hover:fill-pos-yellow"
                    />
                  </div>
                  <span className="leading-[22px] inline-block text-[#8f7319] group-hover:font-medium group-hover:text-pos-yellow cursor-pointer">
                    Cash Register
                  </span>
                </button>
              )}
            </div>

            <div>
              <OptionsMenu className="relative top-0">
                <OptionButton
                  className="flex flex-row items-start justify-center shrink-0 border-2 border-transparent rounded-md tablet:px-3
                  cursor-pointer"
                >
                  {user ? (
                    <>
                      <div
                        className="h-[35px] w-[35px] flex flex-col items-center justify-center 
                    rounded-full tablet:mr-2 bg-[#8f7319] font-semibold text-pos-yellow"
                      >
                        {`${user.othernames[0]}${user.surname[0]}`}
                      </div>
                      <div className="hidden tablet:flex flex-col items-start justify-center mt-[3px] leading-[0.8]">
                        <span className="font-medium text-pos-black group-hover:text-pos-yellow">
                          {`${user.othernames} ${user.surname}`}
                        </span>
                        <span className="text-[#8f7319] text-sm">
                          {user.email}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="h-[35px] w-[35px] flex flex-col items-center justify-center 
                    rounded-full tablet:mr-2 bg-[#8f7319] font-semibold text-pos-yellow"
                      >
                        SU
                      </div>
                      <div className="hidden tablet:flex flex-col items-start justify-center mt-[3px] leading-[0.8]">
                        <span className="font-medium text-pos-black group-hover:text-pos-yellow">
                          Swiftinflow User
                        </span>
                        <span className="text-[#8f7319] text-sm">
                          user@swiftinflow.com
                        </span>
                      </div>
                    </>
                  )}
                </OptionButton>
                <OptionList
                  className="!min-w-[200px] mt-4 right-0 rounded-xl shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] 
                  bg-white border-[0.5px] border-[#e9ecef] ring-[#e9ecef] ring-opacity-5 focus:outline-none"
                >
                  <OptionItem
                    className="text-gray-700"
                    onClick={() => navigate(paths.PROFILE_URL_PATH)}
                  >
                    Your Profile
                  </OptionItem>
                  <OptionItem className=" text-gray-700" onClick={onSignOut}>
                    Logout
                  </OptionItem>
                </OptionList>
              </OptionsMenu>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navigation;
